// PostHog Analytics Provider - Centralized Configuration
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Initialize PostHog
export const initPostHog = () => {
  if (typeof window !== 'undefined') {
    const apiKey = process.env.REACT_APP_POSTHOG_KEY;
    const apiHost = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
    
    if (apiKey && apiKey !== 'phc_your_posthog_key_here') {
      posthog.init(apiKey, {
        api_host: apiHost,
        person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
        autocapture: {
          dom_event_allowlist: ['click', 'submit'], // Limit to clicks and form submits
          url_allowlist: [window.location.origin], // Only track your domain
        },
        capture_pageview: false, // We'll handle this manually for better control
        capture_pageleave: true,
        session_recording: {
          maskAllInputs: true, // Privacy: mask all input fields
          maskTextSelector: '.sensitive', // Mask elements with this class
        },
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ PostHog loaded');
            posthog.debug(); // Enable debug mode in development
          }
        },
      });
      
      console.log('📊 PostHog Analytics initialized');
    } else {
      console.log('⚠️ PostHog API key not configured - analytics disabled');
    }
  }
};

// Hook for automatic pageview tracking
export const usePostHog = () => {
  const location = useLocation();

  useEffect(() => {
    if (posthog.__loaded) {
      posthog.capture('$pageview');
    }
  }, [location]);
};

// Track custom events
export const trackEvent = (eventName, properties = {}) => {
  if (posthog.__loaded) {
    posthog.capture(eventName, properties);
  }
};

// Track page views manually
export const trackPageView = (pageName, properties = {}) => {
  if (posthog.__loaded) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $pathname: window.location.pathname,
      page_name: pageName,
      ...properties
    });
  }
};

// Identify users
export const identifyUser = (userId, traits = {}) => {
  if (posthog.__loaded) {
    posthog.identify(userId, traits);
  }
};

// Reset user (on logout)
export const resetUser = () => {
  if (posthog.__loaded) {
    posthog.reset();
  }
};

// Feature flags
export const isFeatureEnabled = (flagKey) => {
  if (posthog.__loaded) {
    return posthog.isFeatureEnabled(flagKey);
  }
  return false;
};

// AI Tools specific tracking
export const trackAIToolClick = (toolId, toolName, category, url) => {
  trackEvent('ai_tool_click', {
    tool_id: toolId,
    tool_name: toolName,
    category: category,
    destination_url: url,
    timestamp: new Date().toISOString()
  });
};

export const trackAIToolSearch = (query, resultsCount) => {
  trackEvent('ai_tool_search', {
    query: query,
    results_count: resultsCount,
    timestamp: new Date().toISOString()
  });
};

export const trackAIToolFavorite = (toolId, toolName, action) => {
  trackEvent('ai_tool_favorite', {
    tool_id: toolId,
    tool_name: toolName,
    action: action, // 'add' or 'remove'
    timestamp: new Date().toISOString()
  });
};

export const trackAIToolUpvote = (toolId, toolName, action) => {
  trackEvent('ai_tool_upvote', {
    tool_id: toolId,
    tool_name: toolName,
    action: action, // 'add' or 'remove'
    timestamp: new Date().toISOString()
  });
};

export const trackAIToolSubmission = (toolName, category) => {
  trackEvent('ai_tool_submission', {
    tool_name: toolName,
    category: category,
    timestamp: new Date().toISOString()
  });
};

// Conversion tracking
export const trackConversion = (conversionType, value = 0, properties = {}) => {
  trackEvent('conversion', {
    type: conversionType,
    value: value,
    ...properties
  });
};

// Error tracking
export const trackError = (error, context = {}) => {
  trackEvent('error', {
    error_message: error.message,
    error_stack: error.stack,
    ...context
  });
};

export default posthog;
